import { useQueryClient } from '@tanstack/react-query'
import { useCallback, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { toast } from '@/components/ui/sonner'
import { api } from '@/services/api'
import { type SchemaSession, schemaSessionsApi } from '@/services/api/schemaSessions'
import type { EntityType, RelationType, Schema, ValidationFinding } from '@/services/api/schemas'
import { bindingForSelection, caseOwnedVersions, caseSchemaSelection } from '../case-binding'
import { deleteEdit, forkDefaultName, type PendingSchemaEdit, saveEdit, shouldFork } from '../fork'
import { entityFormFromEntity, pruneDisplayLabel, pruneTimelineExclude } from '../format'
import { caseSchemaStatusKey } from './useCaseSchemaStatus'

/**
 * Context that turns the shared schema editor into the case-scoped variant. When
 * `caseId` is set, the manager reads/writes the `case_settings.schema_id` binding
 * so the gallery can mark the in-use schema and let the user select one. Admin
 * usage passes no context and behaves exactly as before. See ADR-0005.
 */
export interface SchemaEditorContext {
  caseId?: string
  /** Case display name — seeds the `"<case> - <orig>"` default fork name. */
  caseName?: string
  draft?: { sessionId: string; schema: Schema; onChange: (session: SchemaSession) => void }
}

import {
  EMPTY_ENTITY_FORM,
  EMPTY_PATTERN_FORM,
  EMPTY_RELATION_FORM,
  type EntityFormState,
  type PatternFormState,
  type RelationFormState,
  type SchemaPattern,
} from '../types'
import { deleteErrorMessage, extractValidationFindings } from '../validation'

export interface UseSchemaManagerResult {
  /** The schema as the last saved entity / relation / pattern change left it — what to re-check. */
  lastSavedSchema: Schema | null
  // schema list / detail
  schemas: Schema[]
  selectedSchema: Schema | null
  loading: boolean
  selectSchema: (schemaId: string) => Promise<void>

  // case binding (Case Schema page) — undefined caseId ⇒ admin mode
  caseId?: string
  /** The schema the case is pinned to, or null when it follows the global active. */
  caseBindingSchemaId: string | null
  /** Point the case at `schemaId`; selecting the global default clears to null. */
  setCaseBinding: (schemaId: string) => Promise<void>
  /**
   * Create a Person-seeded case schema and auto-bind the case to it. Resolves
   * to the new schema's id (or undefined on failure) so the caller can open its
   * detail immediately.
   */
  createCaseSchema: (name: string) => Promise<string | undefined>
  /**
   * Start the case's schema from an existing global one: a pure copy forked into
   * a case-owned v1, auto-bound. Resolves to the new schema's id (or undefined on
   * failure). Used by the intro "start from a global schema" path (SP-385).
   */
  startFromGlobal: (globalSchemaId: string, name?: string) => Promise<string | undefined>
  /**
   * Re-pick the case's starting point, DELETING the schema it owns today (all
   * versions). `sourceSchemaId` null ⇒ start over from a blank Person-seeded
   * schema. Resolves to the replacement's id (or undefined on failure).
   */
  replaceCaseSchema: (sourceSchemaId: string | null, name?: string) => Promise<string | undefined>
  /** Whether this case already owns a schema — drives intro vs. editor. */
  caseOwnsSchema: boolean
  /** Edit the case schema's description; commits a new version. */
  setCaseDescription: (description: string) => Promise<void>
  /** Schema versions already-processed files ran with ("vN") — reprocess notice. */
  processedVersions: Set<string>
  /** Reload the schema list and the case binding after a change made elsewhere
   * (e.g. the schema agent accepting a draft into the case's lineage). */
  refresh: () => Promise<void>

  // fork-on-edit (case view editing a global schema)
  forkDialogOpen: boolean
  setForkDialogOpen: (open: boolean) => void
  /** Editable default name for the fork modal, `"<case> - <orig>"`. */
  forkName: string
  /** Fork the viewed global schema with the captured pending edit; returns the
   * fork id to open. */
  confirmFork: (name: string) => Promise<string | undefined>

  // search / tabs
  searchQuery: string
  setSearchQuery: (value: string) => void
  activeTab: string
  setActiveTab: (value: string) => void

  // detail-panel selection
  selectedEntityForDetail: EntityType | null
  setSelectedEntityForDetail: (entity: EntityType | null) => void
  selectedRelationForDetail: RelationType | null
  setSelectedRelationForDetail: (relation: RelationType | null) => void
  selectedPatternForDetail: SchemaPattern | null
  setSelectedPatternForDetail: (pattern: SchemaPattern | null) => void

  // entity dialog + form
  entityDialogOpen: boolean
  setEntityDialogOpen: (open: boolean) => void
  editingEntity: EntityType | null
  entityForm: EntityFormState
  setEntityForm: (form: EntityFormState) => void
  isSavingEntity: boolean
  entitySaveFindings: ValidationFinding[] | null
  openEntityDialog: (entity?: EntityType) => void
  saveEntity: () => Promise<void>
  deleteEntity: (entityId: string) => Promise<void>

  // relation dialog + form
  relationDialogOpen: boolean
  setRelationDialogOpen: (open: boolean) => void
  editingRelation: RelationType | null
  relationForm: RelationFormState
  setRelationForm: (form: RelationFormState) => void
  isSavingRelation: boolean
  relationSaveFindings: ValidationFinding[] | null
  openRelationDialog: (relation?: RelationType) => void
  saveRelation: () => Promise<void>
  deleteRelation: (relationId: string) => Promise<void>

  // pattern dialog + form
  patternDialogOpen: boolean
  setPatternDialogOpen: (open: boolean) => void
  editingPattern: SchemaPattern | null
  patternForm: PatternFormState
  setPatternForm: (form: PatternFormState) => void
  patternSaveFindings: ValidationFinding[] | null
  openPatternDialog: (pattern?: SchemaPattern) => void
  savePattern: () => Promise<void>
  deletePattern: (patternId: string) => Promise<void>

  // schema version operations
  activateSchema: (schemaId: string) => Promise<void>
  createNewVersion: (schemaId?: string) => Promise<void>

  // import / delete
  importDialogOpen: boolean
  setImportDialogOpen: (open: boolean) => void
  importSchema: (payload: unknown) => Promise<void>
  deleteTarget: Schema | null
  setDeleteTarget: (schema: Schema | null) => void
  deleteSchema: () => Promise<void>
}

/**
 * Container data + actions for the schema manager. Owns ALL imperative / IO
 * concerns — schema list/detail loading, the editor + dialog draft state for
 * entities, relations and patterns, every create/update/delete/import/activate
 * `api.schemas.*` call, and the version operations — so `SchemaManager` can stay
 * a thin presentational orchestrator.
 */
export function useSchemaManager(context?: SchemaEditorContext): UseSchemaManagerResult {
  const qc = useQueryClient()
  const { t } = useTranslation()
  const caseId = context?.caseId
  const caseName = context?.caseName
  const draft = context?.draft
  const [schemas, setSchemas] = useState<Schema[]>([])
  const [selectedSchema, setSelectedSchema] = useState<Schema | null>(null)
  const [loading, setLoading] = useState(true)
  const [caseBindingSchemaId, setCaseBindingSchemaId] = useState<string | null>(null)
  // Schema versions already-processed files ran with (case mode) — the reprocess
  // notice. "vN" strings, refreshed from the case's extraction runs.
  const [processedVersions, setProcessedVersions] = useState<Set<string>>(() => new Set())
  // Fork-on-edit: when a case edits a global schema, the triggering edit is
  // captured here and replayed onto the fork once the user confirms the name.
  const [forkDialogOpen, setForkDialogOpen] = useState(false)
  const [forkName, setForkName] = useState('')
  const [pendingForkEdit, setPendingForkEdit] = useState<PendingSchemaEdit | null>(null)
  const [searchQuery, setSearchQuery] = useState('')
  const [activeTab, setActiveTab] = useState('entities')

  const [selectedEntityForDetail, setSelectedEntityForDetail] = useState<EntityType | null>(null)
  const [selectedRelationForDetail, setSelectedRelationForDetail] = useState<RelationType | null>(
    null
  )
  const [selectedPatternForDetail, setSelectedPatternForDetail] = useState<SchemaPattern | null>(
    null
  )

  const [entityDialogOpen, setEntityDialogOpen] = useState(false)
  const [relationDialogOpen, setRelationDialogOpen] = useState(false)
  const [patternDialogOpen, setPatternDialogOpen] = useState(false)
  const [editingEntity, setEditingEntity] = useState<EntityType | null>(null)
  const [editingRelation, setEditingRelation] = useState<RelationType | null>(null)
  const [editingPattern, setEditingPattern] = useState<SchemaPattern | null>(null)

  const [isSavingEntity, setIsSavingEntity] = useState(false)
  // The schema each saved entity / relation / pattern change produced, so the
  // editor re-runs the concept checks on it and shows the warnings the save
  // gate lets through (antipatterns are warnings; only errors block the save).
  const [lastSavedSchema, setLastSavedSchema] = useState<Schema | null>(null)
  const markSaved = useCallback((saved: Schema | null) => {
    if (saved) setLastSavedSchema(saved)
  }, [])
  const [isSavingRelation, setIsSavingRelation] = useState(false)
  const [entitySaveFindings, setEntitySaveFindings] = useState<ValidationFinding[] | null>(null)
  const [relationSaveFindings, setRelationSaveFindings] = useState<ValidationFinding[] | null>(null)
  const [patternSaveFindings, setPatternSaveFindings] = useState<ValidationFinding[] | null>(null)

  const [entityForm, setEntityForm] = useState<EntityFormState>(EMPTY_ENTITY_FORM)
  const [relationForm, setRelationForm] = useState<RelationFormState>(EMPTY_RELATION_FORM)
  const [patternForm, setPatternForm] = useState<PatternFormState>(EMPTY_PATTERN_FORM)

  const [importDialogOpen, setImportDialogOpen] = useState(false)
  const [deleteTarget, setDeleteTarget] = useState<Schema | null>(null)

  // ==================== Data Fetching ====================

  const fetchSchemaDetails = useCallback(async (schemaId: string): Promise<Schema | null> => {
    try {
      const data = await api.schemas.get(schemaId)
      setSelectedSchema(data)
      return data
    } catch (err: unknown) {
      console.error(err)
      toast.error('Failed to load schema details')
      return null
    }
  }, [])
  // Selecting a schema only loads it; the loaded schema is for the save paths.
  const selectSchema = useCallback(
    async (schemaId: string) => {
      await fetchSchemaDetails(schemaId)
    },
    [fetchSchemaDetails]
  )

  const fetchSchemas = useCallback(
    async (autoSelect = true) => {
      try {
        // Case mode: globals + this case's own (membership-gated route). Admin: globals only.
        const data = await (caseId ? api.schemas.listForCase(caseId) : api.schemas.list())
        if (caseId) qc.setQueryData(caseSchemaStatusKey(caseId), data)
        setSchemas(data)

        // Select a schema by default. Callers that immediately open a specific
        // schema (e.g. create) pass autoSelect=false to avoid a fire-and-forget
        // detail fetch racing — and overwriting — their own.
        if (autoSelect) {
          if (caseId) {
            // Case mode: the editor shows the case's OWN schema (its latest
            // version) — one schema per case (SP-385). No case schema yet ⇒ leave
            // the selection empty so the intro renders instead of the editor.
            const owned = caseOwnedVersions(data)
            if (owned.length > 0) {
              fetchSchemaDetails(owned[0].id)
            } else {
              setSelectedSchema(null)
            }
          } else {
            const active = data.find((s: Schema) => s.is_active)
            if (active) {
              fetchSchemaDetails(active.id)
            } else if (data.length > 0) {
              fetchSchemaDetails(data[0].id)
            }
          }
        }
      } catch (err: unknown) {
        console.error(err)
        toast.error('Failed to load schemas')
      } finally {
        setLoading(false)
      }
    },
    [fetchSchemaDetails, caseId, qc]
  )

  useEffect(() => {
    if (!draft) void fetchSchemas()
  }, [fetchSchemas, draft])

  useEffect(() => {
    if (!draft) return
    setSelectedSchema(draft.schema)
    setSchemas([draft.schema])
    setLoading(false)
    setSelectedEntityForDetail(
      previous => draft.schema.entity_types.find(item => item.label === previous?.label) ?? null
    )
    setSelectedRelationForDetail(
      previous =>
        draft.schema.relationship_types.find(item => item.label === previous?.label) ?? null
    )
    setSelectedPatternForDetail(null)
  }, [draft])

  // ==================== Case Binding ====================

  // Re-read the case's current schema binding (null ⇒ follows the global active).
  // Used on mount and after operations that can change it server-side — e.g.
  // deleting the in-use schema, whose FK (ON DELETE SET NULL) clears the binding.
  const refreshCaseBinding = useCallback(async () => {
    if (!caseId || draft) return
    try {
      const settings = await api.cases.getCaseSettings(caseId)
      setCaseBindingSchemaId(settings.schema_id)
    } catch (err: unknown) {
      console.error(err)
    }
  }, [caseId, draft])

  useEffect(() => {
    void refreshCaseBinding()
  }, [refreshCaseBinding])

  const setCaseBinding = useCallback(
    async (schemaId: string) => {
      if (!caseId) return
      const { globalDefaultId } = caseSchemaSelection(schemas, caseBindingSchemaId)
      const value = bindingForSelection(schemaId, globalDefaultId)
      try {
        const updated = await api.cases.updateCaseSettings(caseId, { schema_id: value })
        setCaseBindingSchemaId(updated.schema_id)
        toast.success('Case schema updated')
      } catch (err: unknown) {
        console.error(err)
        toast.error('Failed to update case schema')
      }
    },
    [caseId, schemas, caseBindingSchemaId]
  )

  const createCaseSchema = useCallback(
    async (name: string): Promise<string | undefined> => {
      if (!caseId) return undefined
      try {
        // The backend seeds a Person entity and auto-binds the case server-side.
        // Refresh the list + binding marker and hand the new id back so the
        // caller can open its detail immediately.
        const data = await api.schemas.createForCase(caseId, { name })
        toast.success(`Created ${data.name}`)
        // Skip auto-select: the caller opens the new schema, and an auto-select
        // detail fetch would race that and leave the editor stuck on its loader.
        await fetchSchemas(false)
        setCaseBindingSchemaId(data.id)
        return data.id
      } catch (err: unknown) {
        console.error(err)
        toast.error('Failed to create schema')
        return undefined
      }
    },
    [caseId, fetchSchemas]
  )

  const startFromGlobal = useCallback(
    async (globalSchemaId: string, name?: string): Promise<string | undefined> => {
      if (!caseId) return undefined
      try {
        // Pure copy (no edit) of a global schema into a case-owned v1, auto-bound
        // server-side. The case now owns its schema — the intro yields to the
        // editor (SP-385). Selecting it directly + setting the binding marker
        // avoids a flash of "not in use" before the list refresh lands.
        const fork = await api.schemas.fork(caseId, globalSchemaId, { name: name?.trim() || null })
        toast.success(`Created ${fork.name}`)
        await fetchSchemas(false)
        setSelectedSchema(fork)
        setCaseBindingSchemaId(fork.id)
        return fork.id
      } catch (err: unknown) {
        console.error(err)
        const findings = extractValidationFindings(err)
        toast.error(findings ? 'Schema validation failed' : 'Failed to create schema')
        return undefined
      }
    },
    [caseId, fetchSchemas]
  )

  const replaceCaseSchema = useCallback(
    async (sourceSchemaId: string | null, name?: string): Promise<string | undefined> => {
      if (!caseId) return undefined
      try {
        // Re-pick the starting point: the server creates the replacement, rebinds
        // the case and deletes the lineage it owned before — so the list refresh
        // comes back with exactly one case-owned schema again.
        const next = await api.schemas.replaceForCase(caseId, {
          source_schema_id: sourceSchemaId,
          name: name?.trim() || undefined,
        })
        toast.success(`Now using ${next.name}`)
        await fetchSchemas(false)
        setSelectedSchema(next)
        setCaseBindingSchemaId(next.id)
        return next.id
      } catch (err: unknown) {
        console.error(err)
        const findings = extractValidationFindings(err)
        toast.error(findings ? 'Schema validation failed' : 'Failed to change starting point')
        return undefined
      }
    },
    [caseId, fetchSchemas]
  )

  // ==================== Case auto-versioning ====================

  // Every edit to a case-owned schema lands on a fresh immutable version (SP-385):
  // the pending edit is applied server-side onto a new version of the schema the
  // user is viewing, the prior version is left untouched, and the editor switches
  // to the new latest. The binding (in-use-for-extraction) is NOT touched here —
  // it stays pinned until the user explicitly pins a newer version. Throws on a
  // rejected edit (422) so the caller can surface findings inline.
  const versionCaseEdit = useCallback(
    async (edit: PendingSchemaEdit): Promise<Schema> => {
      if (!caseId || !selectedSchema) {
        throw new Error('versionCaseEdit requires a case-owned schema')
      }
      if (draft) {
        const updated = await schemaSessionsApi.editDraft(caseId, draft.sessionId, edit)
        if (!updated.draft) throw new Error('The draft update returned no schema')
        setSelectedSchema(updated.draft)
        setLastSavedSchema(updated.draft)
        draft.onChange(updated)
        return updated.draft
      }
      const next = await api.schemas.createVersionForCase(caseId, selectedSchema.id, { edit })
      setSelectedSchema(next)
      setLastSavedSchema(next)
      // The POST returns the full new version — splice it into the list locally so
      // the version switcher updates without a second round-trip on every edit.
      setSchemas(prev => [...prev.filter(s => s.id !== next.id), next])
      return next
    },
    [caseId, selectedSchema, draft]
  )

  // Edit the case schema's description — a schema-level change, so it commits a
  // new version like any other edit (SP-385).
  const setCaseDescription = useCallback(
    async (description: string) => {
      if (!caseId || !selectedSchema) return
      try {
        const next = await api.schemas.createVersionForCase(caseId, selectedSchema.id, {
          description,
        })
        setSelectedSchema(next)
        setSchemas(prev => [...prev.filter(s => s.id !== next.id), next])
        toast.success('Description updated')
      } catch (err: unknown) {
        console.error(err)
        toast.error('Failed to update description')
      }
    },
    [caseId, selectedSchema]
  )

  // ==================== Reprocess notice (case mode) ====================

  // Schema versions that already-processed files ran with — drives the editor's
  // "reprocess" notice (SP-385). Only the case schema's own runs matter, matched
  // by name; "vN" strings. Refreshed on mount and when the case schema changes.
  const ownedLatestName = caseOwnedVersions(schemas)[0]?.name ?? null
  useEffect(() => {
    if (!caseId || !ownedLatestName || draft) {
      setProcessedVersions(new Set())
      return
    }
    let cancelled = false
    api.cases
      // As far back as the list goes: a version files ran with long ago still counts.
      .getExtractionRuns(caseId, { limit: 500 })
      .then(runs => {
        if (cancelled) return
        const versions = new Set<string>()
        for (const r of runs) {
          if (
            r.schema_name === ownedLatestName &&
            r.schema_version &&
            (r.status === 'completed' || r.status === 'completed_with_errors')
          ) {
            versions.add(r.schema_version)
          }
        }
        setProcessedVersions(versions)
      })
      .catch(() => {
        /* a missing runs list just hides the notice */
      })
    return () => {
      cancelled = true
    }
  }, [caseId, ownedLatestName, draft])

  // ==================== Fork-on-edit ====================

  // If we're a case viewing a GLOBAL schema, capture the edit and open the fork
  // modal instead of mutating the shared schema. Returns true when intercepted,
  // so callers `return` before their in-place api.schemas.* path. Admin and
  // case-owned editing fall through unchanged.
  const interceptForkEdit = useCallback(
    (edit: PendingSchemaEdit): boolean => {
      if (!shouldFork(caseId, selectedSchema)) return false
      setPendingForkEdit(edit)
      setForkName(forkDefaultName(caseName, selectedSchema?.name ?? ''))
      // The entity/relation/pattern dialog (if open) yields to the fork modal.
      setEntityDialogOpen(false)
      setRelationDialogOpen(false)
      setPatternDialogOpen(false)
      setForkDialogOpen(true)
      return true
    },
    [caseId, caseName, selectedSchema]
  )

  const confirmFork = useCallback(
    async (name: string): Promise<string | undefined> => {
      if (!caseId || !selectedSchema || !pendingForkEdit) return undefined
      try {
        const fork = await api.schemas.fork(caseId, selectedSchema.id, {
          name,
          edit: pendingForkEdit,
        })
        toast.success(`Forked into ${fork.name}`)
        await fetchSchemas(false)
        setCaseBindingSchemaId(fork.id)
        setPendingForkEdit(null)
        setForkDialogOpen(false)
        return fork.id
      } catch (err: unknown) {
        console.error(err)
        const findings = extractValidationFindings(err)
        toast.error(findings ? 'Schema validation failed' : 'Failed to fork schema')
        return undefined
      }
    },
    [caseId, selectedSchema, pendingForkEdit, fetchSchemas]
  )

  // ==================== Entity Operations ====================

  const openEntityDialog = useCallback((entity?: EntityType) => {
    setEntitySaveFindings(null)
    if (entity) {
      setEditingEntity(entity)
      setEntityForm(entityFormFromEntity(entity))
    } else {
      setEditingEntity(null)
      setEntityForm(EMPTY_ENTITY_FORM)
    }
    setEntityDialogOpen(true)
  }, [])

  const saveEntity = useCallback(async () => {
    if (!selectedSchema) return
    const edit = saveEdit('entity', entityForm, editingEntity?.id)
    if (interceptForkEdit(edit)) return

    setIsSavingEntity(true)
    try {
      if (caseId) {
        // Case-owned ⇒ commit the edit as a new version; keep the detail pane on
        // the just-edited entity (its row id changes across the version bump).
        const next = await versionCaseEdit(edit)
        setSelectedEntityForDetail(
          next.entity_types.find(e => e.label === entityForm.label) ?? null
        )
      } else if (editingEntity) {
        await api.schemas.updateEntityType(editingEntity.id, entityForm)
        markSaved(await fetchSchemaDetails(selectedSchema.id))
      } else {
        await api.schemas.createEntityType(selectedSchema.id, entityForm)
        markSaved(await fetchSchemaDetails(selectedSchema.id))
      }

      setEntitySaveFindings(null)
      setEntityDialogOpen(false)
      toast.success(t(`schemaAgent.editor.toast.entity.${editingEntity ? 'updated' : 'created'}`))
    } catch (err: unknown) {
      console.error(err)
      const findings = extractValidationFindings(err)
      if (findings) {
        // Keep the dialog open and surface the validation findings inline
        // instead of the generic error toast.
        setEntitySaveFindings(findings)
      } else {
        toast.error(t('schemaAgent.editor.toast.entity.saveFailed'))
      }
    } finally {
      setIsSavingEntity(false)
    }
  }, [
    selectedSchema,
    caseId,
    editingEntity,
    entityForm,
    fetchSchemaDetails,
    interceptForkEdit,
    versionCaseEdit,
    t,
    markSaved,
  ])

  const deleteEntity = useCallback(
    async (entityId: string) => {
      if (!selectedSchema) return
      const edit = deleteEdit('entity', entityId)
      if (interceptForkEdit(edit)) return

      try {
        if (caseId) {
          await versionCaseEdit(edit)
          setSelectedEntityForDetail(null)
        } else {
          await api.schemas.deleteEntityType(entityId)
          markSaved(await fetchSchemaDetails(selectedSchema.id))
        }
        toast.success(t('schemaAgent.editor.toast.entity.deleted'))
      } catch (err: unknown) {
        console.error(err)
        toast.error(deleteErrorMessage(err, 'entity', t))
      }
    },
    [selectedSchema, caseId, fetchSchemaDetails, interceptForkEdit, versionCaseEdit, t, markSaved]
  )

  // ==================== Relation Operations ====================

  const openRelationDialog = useCallback((relation?: RelationType) => {
    setRelationSaveFindings(null)
    if (relation) {
      setEditingRelation(relation)
      setRelationForm({
        label: relation.label,
        description: relation.description || '',
        properties: [...relation.properties],
        timeline_exclude: [...(relation.timeline_exclude ?? [])],
      })
    } else {
      setEditingRelation(null)
      setRelationForm(EMPTY_RELATION_FORM)
    }
    setRelationDialogOpen(true)
  }, [])

  const saveRelation = useCallback(async () => {
    if (!selectedSchema) return
    const edit = saveEdit('relation', relationForm, editingRelation?.id)
    if (interceptForkEdit(edit)) return

    setIsSavingRelation(true)
    try {
      if (caseId) {
        const next = await versionCaseEdit(edit)
        setSelectedRelationForDetail(
          next.relationship_types.find(r => r.label === relationForm.label) ?? null
        )
      } else if (editingRelation) {
        await api.schemas.updateRelationType(editingRelation.id, relationForm)
        markSaved(await fetchSchemaDetails(selectedSchema.id))
      } else {
        await api.schemas.createRelationType(selectedSchema.id, relationForm)
        markSaved(await fetchSchemaDetails(selectedSchema.id))
      }

      setRelationSaveFindings(null)
      setRelationDialogOpen(false)
      toast.success(
        t(`schemaAgent.editor.toast.relation.${editingRelation ? 'updated' : 'created'}`)
      )
    } catch (err: unknown) {
      console.error(err)
      const findings = extractValidationFindings(err)
      if (findings) {
        // Keep the dialog open and surface the validation findings inline.
        setRelationSaveFindings(findings)
      } else {
        toast.error(t('schemaAgent.editor.toast.relation.saveFailed'))
      }
    } finally {
      setIsSavingRelation(false)
    }
  }, [
    selectedSchema,
    caseId,
    editingRelation,
    relationForm,
    fetchSchemaDetails,
    interceptForkEdit,
    versionCaseEdit,
    t,
    markSaved,
  ])

  const deleteRelation = useCallback(
    async (relationId: string) => {
      if (!selectedSchema) return
      const edit = deleteEdit('relation', relationId)
      if (interceptForkEdit(edit)) return

      try {
        if (caseId) {
          await versionCaseEdit(edit)
        } else {
          await api.schemas.deleteRelationType(relationId)
          markSaved(await fetchSchemaDetails(selectedSchema.id))
        }
        setSelectedRelationForDetail(null)
        toast.success(t('schemaAgent.editor.toast.relation.deleted'))
      } catch (err: unknown) {
        console.error(err)
        toast.error(deleteErrorMessage(err, 'relation', t))
      }
    },
    [selectedSchema, caseId, fetchSchemaDetails, interceptForkEdit, versionCaseEdit, t, markSaved]
  )

  // ==================== Pattern Operations ====================

  const openPatternDialog = useCallback((pattern?: SchemaPattern) => {
    setPatternSaveFindings(null)
    if (pattern) {
      setEditingPattern(pattern)
      setPatternForm({
        source_type: pattern.source_type,
        relationship_type: pattern.relationship_type,
        target_type: pattern.target_type,
        description: pattern.description || '',
      })
    } else {
      setEditingPattern(null)
      setPatternForm(EMPTY_PATTERN_FORM)
    }
    setPatternDialogOpen(true)
  }, [])

  const savePattern = useCallback(async () => {
    if (!selectedSchema) return
    const edit = saveEdit('pattern', patternForm, editingPattern?.id)
    if (interceptForkEdit(edit)) return

    try {
      if (caseId) {
        await versionCaseEdit(edit)
      } else if (editingPattern) {
        await api.schemas.updatePattern(editingPattern.id, patternForm)
        markSaved(await fetchSchemaDetails(selectedSchema.id))
      } else {
        await api.schemas.createPattern(selectedSchema.id, patternForm)
        markSaved(await fetchSchemaDetails(selectedSchema.id))
      }

      setPatternSaveFindings(null)
      setPatternDialogOpen(false)
      setSelectedPatternForDetail(null)
      toast.success(t(`schemaAgent.editor.toast.pattern.${editingPattern ? 'updated' : 'created'}`))
    } catch (err: unknown) {
      console.error(err)
      const findings = extractValidationFindings(err)
      if (findings) {
        // Keep the dialog open and surface the validation findings inline.
        setPatternSaveFindings(findings)
      } else {
        toast.error(t('schemaAgent.editor.toast.pattern.saveFailed'))
      }
    }
  }, [
    selectedSchema,
    caseId,
    editingPattern,
    patternForm,
    fetchSchemaDetails,
    interceptForkEdit,
    versionCaseEdit,
    t,
    markSaved,
  ])

  const deletePattern = useCallback(
    async (patternId: string) => {
      if (!selectedSchema) return
      const edit = deleteEdit('pattern', patternId)
      if (interceptForkEdit(edit)) return

      try {
        if (caseId) {
          await versionCaseEdit(edit)
        } else {
          await api.schemas.deletePattern(patternId)
          markSaved(await fetchSchemaDetails(selectedSchema.id))
        }
        setSelectedPatternForDetail(null)
        toast.success(t('schemaAgent.editor.toast.pattern.deleted'))
      } catch (err: unknown) {
        console.error(err)
        toast.error(deleteErrorMessage(err, 'pattern', t))
      }
    },
    [selectedSchema, caseId, fetchSchemaDetails, interceptForkEdit, versionCaseEdit, t, markSaved]
  )

  // ==================== Schema Version Operations ====================

  const activateSchema = useCallback(
    async (schemaId: string) => {
      try {
        await api.schemas.activate(schemaId)
        toast.success('Schema activated')
        fetchSchemas()
      } catch (err: unknown) {
        console.error(err)
        toast.error('Failed to activate schema')
      }
    },
    [fetchSchemas]
  )

  const createNewVersion = useCallback(
    async (schemaId?: string) => {
      const targetId = schemaId ?? selectedSchema?.id
      if (!targetId) return

      try {
        // Case mode keeps the new version case-owned (the backend carries
        // ownership forward); admin stays on the global endpoint.
        const data = caseId
          ? await api.schemas.createVersionForCase(caseId, targetId)
          : await api.schemas.createVersion(targetId)
        toast.success(`Version ${data.version} created`)
        await fetchSchemas(false)
        fetchSchemaDetails(data.id)
      } catch (err: unknown) {
        console.error(err)
        toast.error('Failed to create new version')
      }
    },
    [selectedSchema, caseId, fetchSchemas, fetchSchemaDetails]
  )

  const deleteSchema = useCallback(async () => {
    if (!deleteTarget) return
    try {
      await api.schemas.delete(deleteTarget.id)
      toast.success(`Deleted ${deleteTarget.name} v${deleteTarget.version}`)
      const wasSelected = selectedSchema?.id === deleteTarget.id
      setDeleteTarget(null)
      if (wasSelected) setSelectedSchema(null)
      await fetchSchemas()
      // Deleting the in-use schema clears the binding server-side (FK ON DELETE
      // SET NULL); re-read it so the case falls back to the global default.
      await refreshCaseBinding()
    } catch (err: unknown) {
      console.error(err)
      toast.error('Failed to delete schema')
    }
  }, [deleteTarget, selectedSchema, fetchSchemas, refreshCaseBinding])

  const importSchema = useCallback(
    async (payload: unknown) => {
      const typed = payload as Parameters<typeof api.schemas.importFromJson>[0]
      // Case mode imports a case-owned schema (hidden globally); admin imports global.
      const data = caseId
        ? await api.schemas.importForCase(caseId, typed)
        : await api.schemas.importFromJson(typed)
      toast.success(`Imported ${data.name} v${data.version}`)
      await fetchSchemas(false)
      fetchSchemaDetails(data.id)
    },
    [caseId, fetchSchemas, fetchSchemaDetails]
  )

  // Derived: whether this case already owns a schema (one per case).
  const caseOwnsSchema = caseId ? caseOwnedVersions(schemas).length > 0 : false

  const refresh = useCallback(async () => {
    await Promise.all([fetchSchemas(false), refreshCaseBinding()])
  }, [fetchSchemas, refreshCaseBinding])

  return {
    lastSavedSchema,
    refresh,
    schemas,
    selectedSchema,
    loading,
    selectSchema,
    caseId,
    caseBindingSchemaId,
    setCaseBinding,
    createCaseSchema,
    startFromGlobal,
    replaceCaseSchema,
    caseOwnsSchema,
    setCaseDescription,
    processedVersions,
    forkDialogOpen,
    setForkDialogOpen,
    forkName,
    confirmFork,
    searchQuery,
    setSearchQuery,
    activeTab,
    setActiveTab,
    selectedEntityForDetail,
    setSelectedEntityForDetail,
    selectedRelationForDetail,
    setSelectedRelationForDetail,
    selectedPatternForDetail,
    setSelectedPatternForDetail,
    entityDialogOpen,
    setEntityDialogOpen,
    editingEntity,
    entityForm,
    setEntityForm,
    isSavingEntity,
    entitySaveFindings,
    openEntityDialog,
    saveEntity,
    deleteEntity,
    relationDialogOpen,
    setRelationDialogOpen,
    editingRelation,
    relationForm,
    setRelationForm,
    isSavingRelation,
    relationSaveFindings,
    openRelationDialog,
    saveRelation,
    deleteRelation,
    patternDialogOpen,
    setPatternDialogOpen,
    editingPattern,
    patternForm,
    setPatternForm,
    patternSaveFindings,
    openPatternDialog,
    savePattern,
    deletePattern,
    activateSchema,
    createNewVersion,
    importDialogOpen,
    setImportDialogOpen,
    importSchema,
    deleteTarget,
    setDeleteTarget,
    deleteSchema,
  }
}

export { pruneDisplayLabel, pruneTimelineExclude }
